import React, { useMemo, useRef, useState } from "react";
import { Tooltip } from "@mui/material";
import { CheckCheck, Eye, Pencil, Plus, Trash2, UserCheck } from "lucide-react";
import moment from "moment";

import { AddTaskModal } from "components/AddTaskModal/AddTaskModal";
import { DisplayTaskModal } from "components/Modal/DisplayTaskModal/DisplayTaskModal";
import { ConfirmDialog } from "components/ui/ConfirmDialog";
import { DataTable } from "components/ui/DataTable";
import { Pill } from "components/ui/Pill";
import { PILL_COLORS } from "components/ui/Pill/pillColors";
import { TabSwitcher } from "components/ui/TabSwitcher";
import { useClaimTask } from "features/comunications/hooks/useClaimTask";
import { useCompleteTask } from "features/comunications/hooks/useCompleteTask";
import { useDeleteTask } from "features/comunications/hooks/useDeleteTask";
import { usePatientTasks } from "features/comunications/hooks/usePatientTasks";
import { canClaimTask, getTaskAssignmentLabel } from "features/comunications/utils/formatTaskAssignment";
import { SimplePatientPageLayout } from "features/patients/components/SimplePatientPageLayout/SimplePatientPageLayout";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { useDebounce } from "hooks/useDebounce";

import styles from "./PatientTasksPage.module.css";

const ClampedTitle = ({ text, className }) => {
  const ref = useRef(null);
  const [tooltip, setTooltip] = useState("");

  return (
    <Tooltip title={tooltip} placement="top" enterDelay={200}>
      <span
        ref={ref}
        className={className}
        onMouseEnter={() => {
          const el = ref.current;
          if (el && el.scrollHeight > el.clientHeight) setTooltip(text);
        }}
        onMouseLeave={() => setTooltip("")}
      >
        {text}
      </span>
    </Tooltip>
  );
};

const PRIORITY_LABELS = { 0: "High", 1: "Medium", 2: "Low" };
const PRIORITY_PILL = {
  0: PILL_COLORS.reactionYes,
  1: PILL_COLORS.statusWarning,
  2: PILL_COLORS.reactionNo,
};

const STATUS_LABELS = {
  Open: { label: "Open", color: PILL_COLORS.statusWarning },
  "In Progress": { label: "In Progress", color: PILL_COLORS.statusInfo },
  Complete: { label: "Complete", color: PILL_COLORS.statusSuccess },
};

const TAB_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
];

const PatientTasksPage = () => {
  const patientId = useCurrentPatientId();
  const { data: patient } = usePatientData(patientId);

  const [activeTab, setActiveTab] = useState("open");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTask, setSelectedTask] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [taskToComplete, setTaskToComplete] = useState(null);
  const [taskToClaim, setTaskToClaim] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const debouncedSearch = useDebounce(searchTerm);

  const { data: paginatedData, isLoading } = usePatientTasks({
    patientId,
    page: currentPage,
    rows: itemsPerPage,
    searchTerm: debouncedSearch,
  });

  const { mutate: deleteTask, isPending: isDeleting } = useDeleteTask();
  const { mutate: completeTask, isPending: isCompleting } = useCompleteTask();
  const { mutate: claimTask, isPending: isClaiming } = useClaimTask();
  const { data: staffInfo } = useStaffInfo();

  const filteredTasks = useMemo(() => {
    const allTasks = paginatedData?.data || paginatedData || [];

    if (activeTab === "open") {
      return allTasks.filter((task) => task.status !== "Complete");
    }

    return allTasks.filter((task) => task.status === "Complete");
  }, [paginatedData, activeTab]);

  const totalRecords = filteredTasks.length;

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const columns = useMemo(
    () => [
      {
        id: "dueDate",
        header: "Due",
        width: "90px",
        render: (item) => (
          <span className={styles.dateCell}>
            {item.dueDate ? moment(item.dueDate).format("DD-MM-YY") : "—"}
          </span>
        ),
      },
      {
        id: "title",
        header: "Task",
        width: "minmax(150px, 1fr)",
        render: (item) => (
          <div className={styles.titleCell}>
            {item.taskRead === false && (
              <div className={styles.unreadIndicator} title="Unread task" />
            )}
            <ClampedTitle text={item.title} className={styles.taskTitle} />
          </div>
        ),
      },
      {
        id: "taskCategory",
        header: "Type",
        width: "100px",
        render: (item) => item.taskCategory || "—",
      },
      {
        id: "priority",
        header: "Priority",
        width: "80px",
        render: (item) =>
          PRIORITY_LABELS[item.priority] ? (
            <Pill {...PRIORITY_PILL[item.priority]}>
              {PRIORITY_LABELS[item.priority]}
            </Pill>
          ) : (
            "—"
          ),
      },
      {
        id: "status",
        header: "Status",
        width: "100px",
        render: (item) => {
          const statusConfig = STATUS_LABELS[item.status];

          return statusConfig ? (
            <Pill {...statusConfig.color}>{statusConfig.label}</Pill>
          ) : (
            item.status || "—"
          );
        },
      },
      ...(activeTab === "closed"
        ? [
            {
              id: "closedDate",
              header: "Closed",
              width: "90px",
              render: (item) => (
                <span className={styles.dateCell}>
                  {item.closedDate ? moment(item.closedDate).format("DD-MM-YY") : "—"}
                </span>
              ),
            },
          ]
        : []),
      {
        id: "assignedTo",
        header: "Assigned To",
        width: "minmax(100px, 150px)",
        render: (item) => getTaskAssignmentLabel(item),
      },
      {
        id: "actions",
        header: "",
        width: "130px",
        render: (item) => (
          <div className={styles.taskActions}>
            {item.status !== "Complete" && (
              <Tooltip title="Mark as completed" placement="top" enterDelay={200}>
                <button
                  className={styles.taskActionBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTaskToComplete(item);
                  }}
                >
                  <CheckCheck size={16} />
                </button>
              </Tooltip>
            )}
            {canClaimTask(item, staffInfo?.staffMember) && (
              <Tooltip title="Claim task" placement="top" enterDelay={200}>
                <button
                  className={styles.taskActionBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTaskToClaim(item);
                  }}
                >
                  <UserCheck size={16} />
                </button>
              </Tooltip>
            )}
            <Tooltip title="View" placement="top" enterDelay={200}>
              <button
                className={styles.taskActionBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedTask(item);
                }}
              >
                <Eye size={16} />
              </button>
            </Tooltip>
            <Tooltip title="Edit" placement="top" enterDelay={200}>
              <button
                className={styles.taskActionBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditTask(item);
                }}
              >
                <Pencil size={16} />
              </button>
            </Tooltip>
            <Tooltip title="Delete" placement="top" enterDelay={200}>
              <button
                className={`${styles.taskActionBtn} ${styles.taskActionBtnDanger}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setTaskToDelete(item);
                }}
              >
                <Trash2 size={16} />
              </button>
            </Tooltip>
          </div>
        ),
      },
    ],
    [staffInfo, activeTab, setTaskToComplete, setTaskToClaim, setSelectedTask, setEditTask, setTaskToDelete]
  );

  return (
    <SimplePatientPageLayout>
      <div className={styles.contentWrapper}>
        {/* Heading and its action first, then the switcher, then the list — the same
            order the other patient screens use. The action is a text button, matching
            "Add new prescription" and the rest. */}
        <div className={styles.header}>
          <h3 className={styles.title}>Tasks</h3>
          <AddTaskModal
            patient={patient}
            buttonText="Add Task"
            buttonEndIcon={Plus}
            buttonVariant="text"
          />
        </div>

        <TabSwitcher
          options={TAB_OPTIONS}
          value={activeTab}
          onChange={handleTabChange}
          fullWidth
        />

        <DataTable
          columns={columns}
          data={filteredTasks}
          getRowId={(row) => row.id}
          isLoading={isLoading}
          emptyTitle={activeTab === "open" ? "No open tasks" : "No closed tasks"}
          emptyText={activeTab === "open" ? "This patient has no open tasks." : "This patient has no closed tasks."}
          search={{
            value: searchTerm,
            onChange: (value) => {
              setSearchTerm(value);
              setCurrentPage(1);
            },
            placeholder: "Search tasks...",
          }}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          totalCount={totalRecords}
          rowsValue={itemsPerPage}
          onRowsChange={(value) => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}
          rowsOptions={[10, 20, 50, 100]}
          onRowClick={(item) => setSelectedTask(item)}
        />
      </div>

      {selectedTask && (
        <DisplayTaskModal
          taskId={selectedTask.id}
          task={selectedTask}
          isOpen={!!selectedTask}
          closeModal={() => setSelectedTask(null)}
        />
      )}

      <AddTaskModal
        task={editTask}
        patient={patient}
        modalTitle="Edit Task"
        buttonText="Edit Task"
        noButton
        isOpen={!!editTask}
        setIsOpen={(open) => {
          if (!open) setEditTask(null);
        }}
      />

      <ConfirmDialog
        isOpen={!!taskToDelete}
        onClose={() => setTaskToDelete(null)}
        onConfirm={() => {
          if (taskToDelete) {
            deleteTask(taskToDelete.id, {
              onSuccess: () => setTaskToDelete(null),
            });
          }
        }}
        isLoading={isDeleting}
        title="Delete task"
        message="Are you sure you want to delete this task? This action cannot be undone."
        confirmLabel="Delete"
      />

      <ConfirmDialog
        isOpen={!!taskToComplete}
        onClose={() => setTaskToComplete(null)}
        onConfirm={() => {
          if (taskToComplete) {
            completeTask(taskToComplete.id, {
              onSuccess: () => setTaskToComplete(null),
            });
          }
        }}
        isLoading={isCompleting}
        title="Mark as completed"
        message="Are you sure you want to mark this task as completed?"
        confirmLabel="Mark completed"
      />

      <ConfirmDialog
        isOpen={!!taskToClaim}
        onClose={() => setTaskToClaim(null)}
        onConfirm={() => {
          if (taskToClaim) {
            claimTask(taskToClaim.id, {
              onSuccess: () => setTaskToClaim(null),
            });
          }
        }}
        isLoading={isClaiming}
        title="Claim task"
        message="This will assign the task only to you. Continue?"
        confirmLabel="Claim task"
      />
    </SimplePatientPageLayout>
  );
};

export { PatientTasksPage };
